/* eslint-disable react/require-default-props */
/* eslint-disable no-prototype-builtins */
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { VariableSizeList } from 'react-window';

import getCategories from '../api/intergration/pim/categories/gets/getCategories';
import { getQueryParam } from '../utils/queryParams';

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
    fontSize: '14px',
  };

  if (dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      {dataSet[1].CategoryFullPath}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const useStyles = makeStyles({
  paper: {
    boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important',
  },
});

// Adapter for react-window
const ListboxComponent = React.forwardRef((props, ref) => {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (child.hasOwnProperty('group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const StyledPopper = styled(Popper)({
  width: '600px !important',
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

export default function VirtualizedIntergrationCategoryAutocomplete({
  label,
  value,
  onChange,
  multiple,
  className,
  hideAllOption,
  size,
  helperText,
  siteCode = 'JD',
  selectedOnlyMapped = false,
  limitTags = 1000,
}) {
  const classes = useStyles();
  const [categories, setCategories] = React.useState([
    {
      Id: '',
      CategoryName: 'All',
      CategoryFullPath: 'All',
    },
  ]);
  const [selectedValue, setSelectedValue] = React.useState([]);

  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    getCategories(siteCode, selectedOnlyMapped, 1, 200)
      .then((res) => {
        if (res) {
          setCategories([
            {
              Id: '',
              CategoryName: 'All',
              CategoryFullPath: 'All',
            },
            ...res.data.Data,
          ]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err.message);
        setLoading(false);
      });
  }, [siteCode, selectedOnlyMapped]);

  React.useEffect(() => {
    const categoryIdsFromQuery = getQueryParam('categories');
    if (categoryIdsFromQuery !== '') {
      const selected = categoryIdsFromQuery.split(',').map((id) => categories.find((obj) => obj.PimCategoryId === Number(id)));
      if (!selected.includes(undefined)) {
        setSelectedValue(selected);
      }
    }
  }, [categories]);

  React.useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const getOptionLabel = (option) => {
    if (option.CategoryFullPath == null) {
      const foundCategory = categories
        .find((category) => category.PimCategoryId === option.PimCategoryId);
      return foundCategory?.CategoryFullPath;
    }
    return option.CategoryFullPath;
  };

  return (
    <Autocomplete
      id="virtualize-category-autocomplete"
      loading={loading}
      loadingText="Loading..."
      size={size}
      multiple={multiple}
      value={selectedValue}
      disableListWrap
      PopperComponent={StyledPopper}
      ListboxComponent={ListboxComponent}
      className={className}
      getOptionLabel={getOptionLabel}
      options={
        (categories.sort((a, b) => -b.CategoryFullPath.localeCompare(a.CategoryFullPath))).filter(
          (x) => {
            if (hideAllOption) {
              return x.CategoryName !== 'All';
            }
            return x;
          },
        )
      }
      groupBy={(option) => option.CategoryFullPath.split('/')[0]}
      onChange={onChange}
      limitTags={limitTags}
      renderInput={(params) => <TextField {...params} label={label} helperText={helperText} />}
      renderOption={(props, option) => [props, option]}
      renderGroup={(params) => params}
      classes={{ paper: classes.paper }}
    />
  );
}
